import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function UserSmtpForm({
  selected,
  selectedUser,
  selectedLocation,
  locationsList,
  refreshFunc,
  showModal,
}) {
  const dispatch = useDispatch();

  /* ******************** All States ************************* */

  const [smtpSettings, setSmtpSettings] = useState({
    smtpName: "",
    smtpEmail: "",
    smtpPassword: "",
  });

  const [loading, setLoading] = useState(false);

  const [selectedLocations, setSelectedLocations] = useState([]);

  /* ******************** All Functions ************************* */

  const toggleLocationSelection = (locationId) => {
    setSelectedLocations((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((id) => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  const HandleAdd = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.post(
        "/user/smtp",
        {
          ...smtpSettings,
          userId: selectedUser.id,
          locationIdList: selectedLocations,
        },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        // Handle success scenario
        await refreshFunc();
        setSmtpSettings({
          smtpName: "",
          smtpEmail: "",
          smtpPassword: "",
        });
        setSelectedLocations([]);
        dispatch(
          setNotification({
            message: data?.message || "Smtp added successfully",
            type: "success",
          }),
        );
        showModal(false);
      } else {
        throw new Error(data?.message || "Failed to add project");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const HandleUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.patch(
        `/user/smtp`,
        {
          id: selected.id,
          ...smtpSettings,
          userId: selectedUser.id,
          locationIdList: selectedLocations,
        },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        await refreshFunc();
        dispatch(
          setNotification({
            message: data?.message || "Smtp updated successfully",
            type: "success",
          }),
        );
        showModal(false);
      } else {
        throw new Error(data?.message || "Failed to update smtp");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  /* ******************** All UseEffects ************************* */

  useEffect(() => {
    if (selected) {
      setSmtpSettings({
        smtpName: selected.smtpName || "",
        smtpEmail: selected.smtpEmail || "",
        smtpPassword: selected.smtpPassword || "",
      });
      // Set selected locations if they exist
      if (selected.locationIdList && Array.isArray(selected.locationIdList)) {
        setSelectedLocations(selected.locationIdList);
      } else if (selected.locations && Array.isArray(selected.locations)) {
        // In case locations are returned as objects with id property
        setSelectedLocations(selected.locations.map((loc) => loc.id));
      }
    }
  }, [selected]);

  return (
    <div className="p-1">
      {loading && <Spinner />}
      <form onSubmit={selected ? HandleUpdate : HandleAdd}>
        <div className="mb-2 border-bottom border-1 d-flex justify-content-between align-items-center ">
          <h4 className="my-0">Smtp Settings</h4>
        </div>

        <div className="row">
          {/* Left Column - Form Fields */}
          <div className="col-md-6">
            <div className="mt-2">
              <label htmlFor="smtpName">SMTP Name</label>
              <input
                type="text"
                id="smtpName"
                placeholder="Enter Email Name"
                className="form-control"
                value={smtpSettings.smtpName}
                onChange={(e) =>
                  setSmtpSettings({
                    ...smtpSettings,
                    smtpName: e.target.value,
                  })
                }
              />
            </div>
            <div className="mt-3">
              <label htmlFor="smtpEmail">SMTP Email</label>
              <input
                type="email"
                id="smtpEmail"
                placeholder="Enter Email"
                className="form-control"
                value={smtpSettings.smtpEmail}
                onChange={(e) =>
                  setSmtpSettings({
                    ...smtpSettings,
                    smtpEmail: e.target.value,
                  })
                }
              />
            </div>
            <div className="mt-3">
              <label htmlFor="smtpPassword">SMTP Password</label>
              <input
                type="password"
                id="smtpPassword"
                placeholder="Enter Password"
                className="form-control"
                value={smtpSettings.smtpPassword}
                onChange={(e) =>
                  setSmtpSettings({
                    ...smtpSettings,
                    smtpPassword: e.target.value,
                  })
                }
              />
            </div>
          </div>

          {/* Right Column - Locations Multi-Select */}
          <div className="col-md-6">
            <div className="mt-2">
              <label className="mb-2">Select Locations</label>
              <div
                className="border rounded p-2"
                style={{
                  maxHeight: "280px",
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {locationsList && locationsList.length > 0 ? (
                  locationsList.map((location) => (
                    <div key={location.id} className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`location-${location.id}`}
                        checked={selectedLocations.includes(location.id)}
                        onChange={() => toggleLocationSelection(location.id)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`location-${location.id}`}
                        style={{ cursor: "pointer" }}
                      >
                        {location.locationName ||
                          location.name ||
                          `Location ${location.id}`}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-muted mb-0">No locations available</p>
                )}
              </div>
              <small className="text-muted">
                {selectedLocations.length} location(s) selected
              </small>
            </div>
          </div>
        </div>

        <div className="text-end mt-3">
          <button
            type="button"
            onClick={() => showModal(false)}
            className="btn btn-secondary btn-sm me-2"
          >
            Close
          </button>
          <button type="submit" className="btn btn-sm btn-success">
            {selected ? "Update" : "Add"}
          </button>
        </div>
      </form>
    </div>
  );
}
